/**
 * Selectable Card that can be used across application
 *
 * @module views/components/SelectableCard
 * @memberof -Common
 */

import React, { useCallback } from 'react';

import PropTypes, { string } from 'prop-types';
import { SelectableCard as PalSelectableCard } from 'web-palette/dist/components/SelectableCard/SelectableCard';
import { SelectableCardDefaultContent as PalSelectableCardDefaultContent } from 'web-palette/dist/components/SelectableCardDefaultContent/SelectableCardDefaultContent';

import { useLocalRef } from '@ulta/core/hooks/useLocalRef/useLocalRef';
import { useOverlay } from '@ulta/core/providers/OverlayProvider/OverlayProvider';
import { processClientActionType } from '@ulta/core/utils/clientActionProcessor/clientActionProcessor';
import { constants } from '@ulta/core/utils/constants/constants';
import datacapture from '@ulta/core/utils/datacapture/datacapture';
import { spreadProps } from '@ulta/core/utils/dom/dom';
import { handleEmptyObjects } from '@ulta/core/utils/handleEmptyObjects/handleEmptyObjects';

import * as utils from './SelectableCard';

/**
 * Represents a SelectableCard component
 *
 * @method
 * @param {SelectableCardProps} props - React properties passed from composition
 * @returns SelectableCard
 */
export const SelectableCard = React.forwardRef( ( props, ref ) => {
  const {
    action,
    alignment,
    ariaDescribedBy,
    checked,
    children,
    crossButtonVisibility,
    disabled = defaultProps.disabled,
    handleOnchange,
    icon,
    id,
    inputType = defaultProps.inputType,
    isSelected,
    name,
    style,
    subTitle,
    subTitleTextColor = defaultProps.subTitleTextColor,
    subTitleTextStyle = defaultProps.subTitleTextStyle,
    supportingText,
    supportingTextColor = defaultProps.supportingTextColor,
    supportingTextStyle = defaultProps.supportingTextStyle,
    tabIndex,
    title,
    titleTextColor = defaultProps.titleTextColor,
    titleTextStyle = defaultProps.titleTextStyle,
    value,
    variant = defaultProps.variant
  } = props;

  const localRef = useLocalRef( { ref } );

  const { openOverlay, isOverlay } = useOverlay();
  const handleChange = useCallback(
    utils.composeOnChange(
      {
        action,
        radioRef: localRef,
        alignment,
        crossButtonVisibility,
        checked,
        value
      },
      { openOverlay, isOverlay, handleOnchange }
    ),
    [handleOnchange]
  );

  if( !inputType ){
    return null;
  }

  const shouldRenderDefaultContent = variant === 'default';
  const cardStyle = typeof style === 'string' ? style : undefined;

  return (
    <div className='SelectableCard'>
      <PalSelectableCard
        ariaDescribedBy={ ariaDescribedBy }
        inputId={ id }
        ref={ ref }
        inputType={ inputType }
        tabIndex={ tabIndex }
        inputName={ name }
        selected={ checked || isSelected }
        disabled={ disabled }
        inputValue={ value }
        externalCardAction={ handleChange }
        style={ cardStyle }
      >
        { shouldRenderDefaultContent && (
          <PalSelectableCardDefaultContent
            alignment={ alignment }
            disabled={ disabled }
            iconName={ icon }
            subTitle={ subTitle }
            subTitleTextColor={ subTitleTextColor }
            subTitleTextStyle={ subTitleTextStyle }
            supportingText={ supportingText }
            supportingTextColor={ supportingTextColor }
            supportingTextStyle={ supportingTextStyle }
            title={ title }
            titleTextColor={ titleTextColor }
            titleTextStyle={ titleTextStyle }
          />
        ) }
        { children }
      </PalSelectableCard>
    </div>
  );
} );

/**
 * Handles onClick for radio buttons
 * @param {object} data arguments
 * @param {object} data.field Formik field helper
 * @param {object} data.action DXL action
 * @param {object} data.radioRef Radio el ref
 * @param {string} data.alignment Flyout alignment
 * @param {boolean} data.crossButtonVisibility Flyout/modal crossButtonVisibility
 * @param {object} methods arguments
 * @param {function} methods.isOverlay isOverlay
 * @param {function} methods.openOverlay openOverlay
 * @param {function} methods.handleOnchange Caller's onChange callback
 */
export const composeOnChange = ( data, methods ) => ( e ) => {
  const { action, radioRef = {}, alignment, crossButtonVisibility, value } = handleEmptyObjects( data );
  const { isOverlay, openOverlay, handleOnchange } = methods || {};
  const { navigationType, dataCaptureData, clientActionType } = action || {};

  if( typeof isOverlay !== 'function' || !e?.target ){
    return;
  }

  const displayOverlay = isOverlay( navigationType );

  handleOnchange && handleOnchange( value );

  if( !value || value === 'off' ){
    return;
  }

  if( dataCaptureData ){
    datacapture.processEvents( { dataCapture: { ...dataCaptureData } }, constants.DATACAPTURE_EVENT_TYPE.click );
  }

  if( clientActionType ){
    processClientActionType( { action } );
  }

  if( !displayOverlay ){
    return;
  }

  if( typeof openOverlay !== 'function' ){
    return;
  }

  openOverlay( {
    type: action.navigationType,
    opener: radioRef,
    alignment,
    ...( action && { action } ),
    ...( crossButtonVisibility && { crossButtonVisibility } )
  } );
};

/**
 * Property type definitions
 * @typedef SelectableCardProps
 * @type {object}
 * @property {string} alignment - Sets alignment style
 * @property {boolean} crossButtonVisibility - Flag to set class for modal/flyout x button
 * @property {boolean} disabled- Sets disabled or not
 * @property {function} handleOnchange - function triggered on change of selectable card
 * @property {string} icon - Sets icon
 * @property {string} id - Sets ID for input
 * @property {string} inputType - Sets the inputType
 * @property {boolean} isSelected - Sets if the button is selected
 * @property {string} name - Sets name for input
 * @property {string} subTitle - Sets the sub title
 * @property {string} supportingText - Sets supportingText
 * @property {number} tabIndex - Sets tabIndex for input
 * @property {string} title - Sets title
 * @property {string} titleTextStyle - Sets title style
 * @property {string} value - Sets value for input
 * @property {node} children - Child components to render inside the card
 * @property {string} variant - Sets the variant of card ('default' or custom)
 */
export const propTypes = {
  alignment: string,
  ariaDescribedBy: PropTypes.string,
  children: PropTypes.node,
  crossButtonVisibility: PropTypes.bool,
  disabled: PropTypes.bool,
  handleOnchange: PropTypes.func,
  icon: PropTypes.string,
  id: PropTypes.string,
  inputType: PropTypes.oneOf( ['radio', 'checkbox', 'button'] ),
  isSelected: PropTypes.bool,
  name: PropTypes.string,
  style: PropTypes.oneOfType( [PropTypes.string, PropTypes.object] ),
  subTitle: PropTypes.string,
  supportingText: PropTypes.string,
  supportingTextStyle: PropTypes.string,
  tabIndex: PropTypes.number,
  title: PropTypes.string,
  titleTextStyle: string,
  value: PropTypes.string,
  variant: PropTypes.string
};

/**
 * Default values for passed properties
 *
 * @type {object}
 * @property {string} inputType = 'checkbox' - Sets the inputType
 * @property {boolean} disabled = false - Sets disabled or not
 * @property {string} titleTextStyle = 'body-1' - Sets text style for title
 * @property {string} supportingTextStyle = 'body-2' - Sets the text style for support text
 * @property {string} variant = 'default' - Sets default variant
 */
export const defaultProps = {
  disabled: false,
  inputType: 'checkbox',
  subTitleTextColor: 'default',
  subTitleTextStyle: 'label',
  supportingTextColor: 'xsubtle',
  supportingTextStyle: 'body-sm',
  titleTextColor: 'default',
  titleTextStyle: 'body',
  variant: 'default'
};

SelectableCard.propTypes = propTypes;
SelectableCard.defaultProps = defaultProps;
SelectableCard.displayName = 'SelectableCard';

export default SelectableCard;